import { useState } from 'react'
import { Link } from 'react-router'
import { Play, Pause, Settings2, Settings, Activity } from 'lucide-react'
import { toast } from 'sonner'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useApiClient } from '@/shared/api'

import { useDialerCampaign } from '../hooks/useDialerCampaign'
import { CampaignContactLists } from './CampaignContactLists'
import { CallDurations } from './CallDurations'
import { MetricCard } from './MetricCard'
import { DialerIssuesNotification } from './DialerIssuesNotification'

interface CampaignCardProps {
  campaignId: number
  campaignName: string
  isOrgAdmin: boolean
}

export function CampaignCard({ campaignId, campaignName, isOrgAdmin }: CampaignCardProps) {
  const apiClient = useApiClient()
  const {
    campaign,
    initialLoading,
    isOperating,
    toggleCampaignStatus,
    toggleListStatus,
    refreshCampaign,
  } = useDialerCampaign(campaignId)

  const [isLeadResyncing, setIsLeadResyncing] = useState(false)

  const handleResyncLeadsClick = async () => {
    if (isLeadResyncing || !campaign || campaign.isSyncInProgress) return

    setIsLeadResyncing(true)

    try {
      await apiClient('/dialer/campaign/resync', {
        method: 'POST',
        body: JSON.stringify({
          campaignId: campaign.id,
        }),
      })

      toast.success('Lead resync started')
      refreshCampaign()
    } catch (error) {
      console.error('Failed to resync leads:', error)
      toast.error('Failed to resync leads')
    } finally {
      refreshCampaign()
      setIsLeadResyncing(false)
    }
  }

  const handleToggleStatus = async () => {
    try {
      await toggleCampaignStatus()
    } catch {
      toast.error('Failed to update campaign status')
    }
  }

  const isEditable = true

  if (initialLoading || !campaign) {
    return (
      <Card className="border-2 shadow-md hover:shadow-lg transition-shadow duration-300">
        <CardContent className="space-y-6 py-6">
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="rounded-lg bg-muted/50 p-3">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-2 shadow-md hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle>{campaignName}</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/dialer/${campaignId}/status`}>
                <Activity className="h-4 w-4 mr-1" />
                Dialer Status
              </Link>
            </Button>

            {isOrgAdmin && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dialer/${campaignId}/admin`}>
                  <Settings className="h-4 w-4 mr-1" />
                  Admin Settings
                </Link>
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleResyncLeadsClick}
              disabled={!isEditable || isLeadResyncing || isOperating || campaign.isSyncInProgress}
            >
              {isLeadResyncing || campaign.isSyncInProgress ? 'Syncing...' : 'Resync Leads'}
            </Button>

            <Button variant="outline" size="sm" asChild>
              <Link to={`/dialer/${campaignId}/settings`}>
                <Settings2 className="h-4 w-4 mr-1" />
                Settings
              </Link>
            </Button>

            <Button
              variant={campaign.isStarted ? 'outline' : 'default'}
              size="sm"
              onClick={handleToggleStatus}
              disabled={!isEditable || isOperating}
            >
              {campaign.isStarted ? (
                <>
                  <Pause className="h-4 w-4 mr-1" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  Start
                </>
              )}
            </Button>
          </div>
        </div>
        <div className="text-body text-muted-foreground mt-1">
          Active Conferences:{' '}
          <span className="font-medium">{campaign.config.maxActiveConferences}</span> • Max
          Simultaneous Calls:{' '}
          <span className="font-medium">{campaign.config.maxSimultaneousCalls}</span> • Ratio:{' '}
          <span className="font-medium">{campaign.config.ratio}</span> • Max DPS:{' '}
          <span className="font-medium">{campaign.config.maxDialPerSecond}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <DialerIssuesNotification campaign={campaign} />

        <div>
          <h3 className="text-body font-medium mb-3">Campaign Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <MetricCard title="Ringing Calls" value={campaign.metrics?.ringingCalls ?? 0} />
            <MetricCard title="Connected Calls" value={campaign.metrics?.connectedCalls ?? 0} />
            <MetricCard
              title="Connected Transfers"
              value={campaign.metrics?.connectedTransfers ?? 0}
            />
            <MetricCard title="Dialed Today" value={campaign.metrics?.dialedToday ?? 0} />
            <MetricCard title="Connected Today" value={campaign.metrics?.connectedToday ?? 0} />
            <MetricCard
              title="Transferred Today"
              value={campaign.metrics?.transferredToday ?? 0}
            />
            <MetricCard title="Leads in Queue" value={campaign.metrics?.leadsInQueue ?? 0} />
            <MetricCard
              title="Connection Rate"
              value={`${campaign.metrics?.connectionRate ?? 0}%`}
            />
            <MetricCard
              title="Drop Rate"
              value={`${campaign.metrics?.dropRate ?? 0}%`}
              isHighlighted={Number(campaign.metrics?.dropRate ?? 0) > 3}
            />
          </div>
        </div>

        {campaign.callDistribution && (
          <CallDurations callDurations={campaign.callDistribution} />
        )}

        <div>
          <h3 className="text-body font-medium mb-3">Contact Lists</h3>
          <CampaignContactLists
            contactLists={campaign.contactLists || []}
            onToggleContactListItem={toggleListStatus}
            disabled={isOperating}
          />
        </div>
      </CardContent>
    </Card>
  )
}